import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { Plus, Users, ClipboardList, Clock, ChevronRight, TrendingUp, Search, Archive, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { isInactive } from '../utils/activity';
import { getClass3DIconPath, classInitials, getClassGradient } from '../utils/classIcon';
import type { ClassRow } from '../types/sinif';



interface ClassWithCount extends ClassRow {
  studentCount: number;
}

interface RollUp {
  dueTodayCount: number;
  inactiveStudentCount: number;
}

export default function Panel() {
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const [classes, setClasses] = useState<ClassWithCount[] | null>(null);
  const [rollUp, setRollUp] = useState<RollUp | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    if (!user) return;
    const supabase = await getSupabase();

    const { data: classRows } = await supabase
      .from('classes')
      .select('*')
      .eq('teacher_id', user.id)
      .eq('is_archived', showArchived)
      .order('created_at', { ascending: false });

    const classList = (classRows as ClassRow[]) ?? [];

    const withCounts: ClassWithCount[] = await Promise.all(
      classList.map(async c => {
        const { count } = await supabase
          .from('class_students')
          .select('student_id', { count: 'exact', head: true })
          .eq('class_id', c.id)
          .eq('status', 'aktif');
        return { ...c, studentCount: count ?? 0 };
      })
    );

    const todayStr = new Date().toISOString().slice(0, 10);
    const { count: dueTodayCount } = await supabase
      .from('assignments')
      .select('id', { count: 'exact', head: true })
      .eq('teacher_id', user.id)
      .eq('due_date', todayStr)
      .neq('status', 'tamamlandi');

    const { data: activeClassRows } = await supabase
      .from('classes')
      .select('id')
      .eq('teacher_id', user.id)
      .eq('is_archived', false);
    const activeClassIds = (activeClassRows ?? []).map((c: any) => c.id);

    let inactiveStudentCount = 0;
    if (activeClassIds.length > 0) {
      const { data: memberRows } = await supabase
        .from('class_students')
        .select('student_id')
        .in('class_id', activeClassIds)
        .eq('status', 'aktif');
      const studentIds = Array.from(new Set((memberRows ?? []).map((r: any) => r.student_id)));
      if (studentIds.length > 0) {
        const { data: snapshots } = await supabase
          .from('student_snapshots')
          .select('student_id, last_active_at')
          .in('student_id', studentIds);
        const activeMap = new Map((snapshots ?? []).map((s: any) => [s.student_id, s.last_active_at]));
        inactiveStudentCount = studentIds.filter(id => isInactive(activeMap.get(id))).length;
      }
    }

    setClasses(withCounts);
    setRollUp({ dueTodayCount: dueTodayCount ?? 0, inactiveStudentCount });
  };

  useEffect(() => {
    loadData().catch(err => console.error('Panel verisi yüklenemedi:', err));
  }, [user, getSupabase, showArchived]);

  async function unarchiveClass(classId: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const supabase = await getSupabase();
    await supabase.from('classes').update({ is_archived: false }).eq('id', classId);
    loadData();
  }

  const filteredClasses = (classes ?? []).filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-4xl mx-auto">
      <Helmet>
        <title>Öğretmen Paneli | Bitmiyo Sınıf</title>
        <meta name="description" content="Sınıflarınız, öğrenci durumları ve günlük ödev takibi." />
      </Helmet>

      {/* Karşılama Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900 via-brand-700 to-accent-500 p-6 md:p-8 mb-6 shadow-lg">
        {/* Dekoratif halkalar */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-6 -right-2 w-28 h-28 rounded-full bg-white/5" />
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white/70 text-sm font-semibold mb-1">
              Merhaba, {user?.firstName ?? 'Öğretmenim'}
            </p>
            <h1 className="text-2xl md:text-3xl font-black text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              Bugün ne durumdayız?
            </h1>
            <p className="text-white/60 text-sm">
              {classes !== null
                ? `${classes.length} ${showArchived ? 'arşivlenmiş ' : ''}sınıf yönetiyorsunuz`
                : 'Sınıflarınız yükleniyor...'}
            </p>
          </div>
          <Button
            as={Link}
            to="/sinif/yeni"
            className="shrink-0 bg-white/15 hover:bg-white/25 text-white border-0 backdrop-blur-sm shadow-none"
          >
            <Plus size={16} /> Yeni Sınıf
          </Button>
        </div>
      </div>

      {/* Stat Kartları */}
      {rollUp ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="p-5 animate-fade-in-up" style={{ animationDelay: '0ms' }}>
            <ClipboardList size={20} className="text-accent-500 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">
              Bugün Teslim
            </p>
            <p className="text-3xl font-black text-text-primary">{rollUp.dueTodayCount}</p>
          </Card>
          <Card className="p-5 animate-fade-in-up" style={{ animationDelay: '60ms' }}>
            <Clock size={20} className="text-warning-500 mb-3" />
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">
                  3+ Gündür Pasif
                </p>
                <p className="text-3xl font-black text-text-primary">{rollUp.inactiveStudentCount}</p>
              </div>
              {rollUp.inactiveStudentCount > 0 && (
                <Badge variant="warning">Dikkat</Badge>
              )}
            </div>
          </Card>
          <Card className="p-5 animate-fade-in-up" style={{ animationDelay: '120ms' }}>
            <TrendingUp size={20} className="text-success-500 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">
              Aktif Sınıf
            </p>
            <p className="text-3xl font-black text-text-primary">{classes?.length ?? '—'}</p>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-2xl border border-border p-5 h-[112px] animate-shimmer" />
          ))}
        </div>
      )}

      {/* Sınıflar başlık ve Sekmeler */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {showArchived ? (
            <Archive size={15} className="text-text-secondary" />
          ) : (
            <Users size={15} className="text-text-secondary" />
          )}
          <h2 className="text-sm font-black uppercase tracking-widest text-text-secondary">
            {showArchived ? 'Arşivlenmiş Sınıflar' : 'Sınıflarım'}
          </h2>
          <span className="text-[10px] font-black text-text-muted bg-border/60 rounded-full px-2 py-0.5">
            {filteredClasses.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Sekme Butonu */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`text-xs font-bold px-3 py-1.5 rounded-xl border transition-all active:scale-95 flex items-center gap-1.5 ${
              showArchived
                ? 'bg-accent-100 border-accent-300 text-accent-700 dark:bg-accent-500/20 dark:text-accent-400'
                : 'bg-surface border-border text-text-secondary hover:text-text-primary'
            }`}
          >
            <Archive size={14} />
            {showArchived ? 'Aktif Sınıflara Dön' : 'Arşivi Göster'}
          </button>
        </div>
      </div>

      {/* Arama Inputu */}
      {(classes ?? []).length > 2 && (
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Sınıf adı ile ara..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-border bg-surface text-sm outline-none focus:border-accent-400"
          />
        </div>
      )}

      {classes === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1].map(i => (
            <div key={i} className="rounded-2xl h-[150px] animate-shimmer" />
          ))}
        </div>
      )}

      {classes !== null && filteredClasses.length === 0 && (
        <EmptyState
          icon={showArchived ? Archive : Users}
          title={showArchived ? 'Arşivlenmiş sınıf bulunmuyor' : searchQuery ? 'Aramanıza uygun sınıf bulunamadı' : 'Henüz sınıf yok'}
          description={showArchived ? 'Arşive aldığınız sınıflar burada görünür.' : 'İlk sınıfını oluşturarak öğrencilerini davet etmeye başla.'}
          action={
            !showArchived && !searchQuery ? (
              <Button as={Link} to="/sinif/yeni">
                <Plus size={16} /> Yeni Sınıf Oluştur
              </Button>
            ) : undefined
          }
        />
      )}

      {classes !== null && filteredClasses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredClasses.map((c, idx) => {
            const gradient = getClassGradient(c.id);
            return (
              <Link
                key={c.id}
                to={`/sinif/${c.id}`}
                className="group relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
              >
                {/* Gradient arka plan */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradient} ${showArchived ? 'opacity-60 saturate-50' : ''}`} />
                {/* Dekoratif daireler */}
                <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
                <div className="absolute -bottom-4 -right-2 w-20 h-20 rounded-full bg-white/10" />

                <div className="relative p-5">
                  {/* 3D İkon Logo / Harf Avatar */}
                  {getClass3DIconPath(c) ? (
                    <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm p-1.5 flex items-center justify-center mb-4 border border-white/30 shadow-md">
                      <img src={getClass3DIconPath(c)!} alt={c.name} className="w-full h-full object-contain filter drop-shadow" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center font-black text-white text-lg mb-4 border border-white/20 shadow-md">
                      {classInitials(c.name)}
                    </div>
                  )}

                  <p className="font-black text-white text-lg leading-tight mb-1 truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                    {c.name}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1.5 text-white/75 text-sm font-semibold">
                      <Users size={14} />
                      {c.studentCount} öğrenci
                    </div>
                    <div className="flex items-center gap-2">
                      {showArchived ? (
                        <button
                          onClick={e => unarchiveClass(c.id, e)}
                          className="bg-white/25 hover:bg-white/40 text-white text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 backdrop-blur-sm transition-all"
                          title="Arşivden Çıkar"
                        >
                          <RefreshCw size={12} /> Geri Yükle
                        </button>
                      ) : (
                        <>
                          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/20 text-white">
                            {c.requires_approval ? 'Onaylı' : 'Açık'}
                          </span>
                          <ChevronRight size={16} className="text-white/60 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
